/**
 * Follow-up drawer — the follow-up methods: shown, enabled, why not (drawer redesign Phase 6a, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-drawer-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * ── SOURCE OF TRUTH: TODAY'S DRAWER ────────────────────────────────────────────────────────────
 * CreateFollowup.vue (FROZEN) — the action buttons (template :123–262) and their computeds
 * (:2895–2989, :3011–3022, :3906–3983). The design's five methods map onto them:
 *   Phone        "Phone Followup"     — never disabled (see phoneMethod)
 *   Text         "SMS Followup"       — shouldEnableSMSFollowup / smsFollowupDisabledReasons / canSendSMS
 *   Email        "Email Followup"     — shouldEnableEmailFollowup / emailFollowupDisabledReasons
 *   Payment link "Collect Payment"    — Nilay 2026-10-02: the SAME function. Balances only; Moolah or
 *                                       Stripe link by text, and a Stripe Connect site opens the
 *                                       in-office card modal from the same button.
 *   AI call      "AI Call (SIM)"      — Super Admin, recall / unscheduled only
 *
 * ── THE SHARED RULES ARE INPUTS, NEVER RE-IMPLEMENTED HERE ─────────────────────────────────────
 * The caller passes the RESULTS of the shared helpers, computed exactly as today's drawer does:
 *   smsEnabled    = siteCapability.isSmsEnabled(allSettings)          (default-deny)
 *   emailEnabled  = siteCapability.isEmailEnabled(allSettings)
 *   smsOutboundOff = siteCapability.hasSmsNumber(allSettings) && !isSmsEnabled(allSettings)
 *                   (a WhatsApp-only office: SMS is off on purpose — a reason, never the upsell)
 *   textBlock     = textConsent.getTextBlockReason({ patient, timezone: GENERAL_TIMEZONE })
 *   phoneTextable = patientPhone.isTextablePhone(patient.WirelessPhone)
 *   phoneProblem  = patientPhone.describePhoneProblem(patient.WirelessPhone)
 * This file never looks at TxtMsgOk / SmsOptOutAction or a phone number's format itself
 * (create-followup-opt-out / text-consent-wiring / patient-phone-textable guard that).
 * `settingsLoaded` (allSettings truthy) keeps today's fail-OPEN button while settings load: the
 * buttons only test the capability once settings exist, the reasons list always does.
 */

export const METHOD_ORDER = ['phone', 'sms', 'email', 'payment', 'ai'];

/** The design's labels for today's buttons. */
export const METHOD_LABELS = { phone: 'Phone', sms: 'Text', email: 'Email', payment: 'Payment link', ai: 'AI call' };

/** Text and email are not allowed on these follow-up types (:2901, :2914). */
const NO_MESSAGE_TYPES = ['Outstanding Claim', 'Outstanding PreAuth'];

/** The request-to-enable button each method shows when the PRACTICE lacks the feature (template :163, :209). */
export const UPSELL = {
  sms: { featureKey: 'SMS', featureLabel: '2-Way SMS texting' },
  email: { featureKey: 'EMAIL', featureLabel: 'Patient email' },
};

export const MESSAGES = {
  noStatuses: 'You have not set up statuses yet. Please contact your administrator or contact teamio',
  // 2026-10-28: plain statement only — the request button below carries the note that a request does not turn it on.
  smsFeatureOff: 'Texting follow-ups are not set up on your account yet.',
  smsNoMobile: 'Patient does not have a cell phone number. Add a wireless number in your practice management system.',
  smsNotAllowed: 'SMS follow-ups are not allowed for Outstanding Claims and Outstanding Pre-Authorizations.',
  emailFeatureOff: 'Email follow-ups are not set up on your account yet.',
  emailNoAddress: 'Patient does not have an email address. Add an email address in your practice management system.',
  emailNotAllowed: 'Email follow-ups are not allowed for Outstanding Claims and Outstanding Pre-Authorizations.',
  // WhatsApp-only office (SMS_OUTBOUND_DISABLED). WORD FOR WORD siteCapability.SMS_DISABLED_MESSAGE —
  // this file stays import-free, so FollowupDrawerAgreement.spec pins the two equal.
  smsOutboundOff: 'Texting is turned off for this office. Message patients on WhatsApp instead.',
  paymentSmsOff: "Texting is turned off for this office, so a payment link can't be texted.",
  paymentOff: 'Payment integration is not turned on for this practice (Settings → Payment).',
  paymentNoTextingNumber: 'This practice does not have a texting phone number set up yet.',
  paymentNoMobile: 'This patient has no mobile/text number on file to send the payment link to.',
  paymentNoTextedStatus: 'There is no mapping available for "texted" status. Please refer to your Settings or contact Teamio support',
  paymentNoNote: 'Please select a payment template first',
  paymentNoProvider: 'No payment provider is configured for this site. Please set one up in Payment Settings before sending a payment link.',
};

function present(v) {
  return !!v && v !== '';
}

/**
 * Phone. ⚠ Today the button can never look disabled: its class binding is
 * `!addAttemptClass`, and addAttemptClass is a non-empty string in BOTH branches (:3536) — so
 * `enabled` is always true. A click opens the form only when the attempt gate is open
 * (drawerAlerts.attemptGateOpen) and the queue has statuses; with no statuses it toasts.
 */
export function phoneMethod({ hasStatuses, gateOpen } = {}) {
  return {
    key: 'phone', shown: true, enabled: true, reasons: [],
    opensForm: !!(hasStatuses && gateOpen),
    clickError: hasStatuses ? null : MESSAGES.noStatuses,
  };
}

/** Text — the button, its reasons popover, and the click guard (one source each, as today). */
export function smsMethod({ followupType, patient, settingsLoaded, smsEnabled, smsOutboundOff, textBlock, phoneTextable, phoneProblem, hasStatuses, gateOpen } = {}) {
  const p = patient || {};
  const blocked = !!(textBlock && textBlock.blocked);
  const notAllowed = NO_MESSAGE_TYPES.indexOf(followupType) !== -1;
  const enabled = !(!present(p.WirelessPhone) || notAllowed || (settingsLoaded && !smsEnabled) || blocked);

  const reasons = [];
  if (!smsEnabled) {
    reasons.push(smsOutboundOff
      ? { icon: 'fa-ban', text: MESSAGES.smsOutboundOff, isFeatureIssue: true }
      : { icon: 'fa-exclamation-triangle', text: MESSAGES.smsFeatureOff, isFeatureIssue: true });
  }
  if (blocked) reasons.push({ icon: 'fa-user-times', text: textBlock.message, isFeatureIssue: false });
  if (!present(p.WirelessPhone)) reasons.push({ icon: 'fa-mobile', text: MESSAGES.smsNoMobile, isFeatureIssue: false });
  if (notAllowed) reasons.push({ icon: 'fa-ban', text: MESSAGES.smsNotAllowed, isFeatureIssue: false });

  // canSendSMS (:3011) — checked when the form would open: the opt-out message wins, else the phone's problem.
  const canSend = !blocked && !!phoneTextable;
  let clickError = null;
  if (!hasStatuses) clickError = MESSAGES.noStatuses;
  else if (gateOpen && !canSend) clickError = blocked ? textBlock.message : phoneProblem;

  return {
    key: 'sms', shown: true, enabled, reasons,
    upsell: (smsEnabled || smsOutboundOff) ? null : UPSELL.sms,
    showTip: !!smsEnabled && reasons.some(r => !r.isFeatureIssue),
    canSend,
    opensForm: !!(hasStatuses && gateOpen && canSend),
    clickError,
  };
}

/** Email — button + reasons. No opt-out rule for email today. */
export function emailMethod({ followupType, patient, settingsLoaded, emailEnabled, hasStatuses, gateOpen } = {}) {
  const p = patient || {};
  const notAllowed = NO_MESSAGE_TYPES.indexOf(followupType) !== -1;
  const enabled = !(!present(p.Email) || notAllowed || (settingsLoaded && !emailEnabled));

  const reasons = [];
  if (!emailEnabled) reasons.push({ icon: 'fa-exclamation-triangle', text: MESSAGES.emailFeatureOff, isFeatureIssue: true });
  if (!present(p.Email)) reasons.push({ icon: 'fa-envelope', text: MESSAGES.emailNoAddress, isFeatureIssue: false });
  if (notAllowed) reasons.push({ icon: 'fa-ban', text: MESSAGES.emailNotAllowed, isFeatureIssue: false });

  return {
    key: 'email', shown: true, enabled, reasons,
    upsell: emailEnabled ? null : UPSELL.email,
    showTip: !!emailEnabled && reasons.some(r => !r.isFeatureIssue),
    opensForm: !!(hasStatuses && gateOpen),
    clickError: hasStatuses ? null : MESSAGES.noStatuses,
  };
}

/** A Stripe Connect site (provider 'stripe' AND an account id) — today's isStripeSite (:2987). */
export function isStripeConnectSite(site) {
  return !!(site && site.PaymentProvider === 'stripe' && !!site.StripeAccountId);
}

/**
 * Payment link = today's Collect Payment (:2920–2967, :2360). Balances only, for every role.
 * Enabled only with ALL THREE: PAYMENT_ENABLED === '1' (string), a practice texting number
 * (smsEnabled), and a patient mobile — for Moolah AND Stripe (the action is always a text).
 * ⚠ It ignores the attempt gate and the responsible person (the not-responsible alert even hides
 * while its form is open).
 * opens: 'stripeInOffice' (StripePaymentModal) on a Stripe Connect site, else 'smsLinkForm'.
 *
 * bug 4 FIXED (Nilay 2026-10-02): today never consults text consent — on Moolah a balance_payments
 * row is created and the text attempted before the backend refuses an opted-out patient (:1769).
 * Here `textBlock` (textConsent.getTextBlockReason's answer, the SAME input Text gets) disables the
 * link with textConsent's own message, and paymentSendBlock refuses before any store action. The
 * in-office card (Stripe Connect) sends no text, so consent does not gate it.
 */
export function paymentMethod({ followupType, allSettings, smsEnabled, smsOutboundOff, patient, site, textBlock } = {}) {
  const shown = followupType === 'Patient Balances';
  const s = allSettings || {};
  const opens = isStripeConnectSite(site) ? 'stripeInOffice' : 'smsLinkForm';
  const reasons = [];
  if (!(s.PAYMENT_ENABLED && s.PAYMENT_ENABLED.PropertyValue === '1')) reasons.push(MESSAGES.paymentOff);
  if (!smsEnabled) reasons.push(smsOutboundOff ? MESSAGES.paymentSmsOff : MESSAGES.paymentNoTextingNumber);
  const mobile = patient && patient.WirelessPhone;
  if (!(mobile && String(mobile).trim() !== '')) reasons.push(MESSAGES.paymentNoMobile);
  if (opens === 'smsLinkForm' && textBlock && textBlock.blocked) reasons.push(textBlock.message);
  return {
    key: 'payment', shown, enabled: reasons.length === 0, reasons, opens,
  };
}

/** Which send path "Send Payment SMS" takes (collectPayment, :1734): 'moolah', 'stripe' or null. */
export function paymentRoute(site) {
  const provider = site && site.PaymentProvider;
  if (provider === 'moolah') return 'moolah';
  if (provider === 'stripe') return 'stripe';
  return null;
}

/**
 * collectPayment's checks, in order (:1713–1741): the toast it shows instead of sending, or null.
 * `phoneTextable` / `phoneProblem` are patientPhone's answers for the patient's WirelessPhone.
 * bug 4 FIXED: `textBlock` (textConsent.getTextBlockReason) refuses an opted-out patient right after
 * the status check — before the phone, and before any balance_payments row or text (today: none).
 */
export function paymentSendBlock({ smsStatusID, phoneTextable, phoneProblem, note, site, textBlock } = {}) {
  if (!smsStatusID) return MESSAGES.paymentNoTextedStatus;
  if (textBlock && textBlock.blocked) return textBlock.message;
  if (!phoneTextable) return phoneProblem;
  if (!note || note.trim() === '') return MESSAGES.paymentNoNote;
  if (!paymentRoute(site)) return MESSAGES.paymentNoProvider;
  return null;
}

/** AI call (:2969–2980): Super Admin, recall / unscheduled, a wireless number. No opt-out check today. */
export function aiCallMethod({ isSuperAdmin, followupType, patient, inProgress } = {}) {
  const shown = isSuperAdmin === true && (followupType === 'Recall' || followupType === 'Unscheduled Treatment');
  const enabled = present(patient && patient.WirelessPhone);
  return { key: 'ai', shown, enabled, buttonDisabled: !enabled || !!inProgress, reasons: [] };
}

/**
 * The form's send button (template :489, :571, :634, :701). `smsBlocked` is <SmsGuidance>'s
 * over-the-carrier-limit block — the ONLY SMS cost gate; there is no cost prompt (sms-cost-no-prompt).
 */
export function sendButtonDisabled(method, { attemptInProgress, smsBlocked } = {}) {
  if (method === 'sms' || method === 'payment') return !!(attemptInProgress || smsBlocked);
  return !!attemptInProgress;
}

/** The design's footer Save label per method. */
export const SAVE_LABELS = { phone: 'Save attempt', sms: 'Send text', email: 'Send email', payment: 'Send payment link', ai: 'Place AI call' };

/**
 * The footer Save button: the design's "disabled until there is something to save" (an outcome, a
 * message, a template) on top of today's send-button rule (sendButtonDisabled — in progress, and
 * <SmsGuidance>'s hard block on Text / Payment link). Everything else is still checked on click by
 * saveBlockReason / paymentSendBlock, which say WHY.
 */
export function saveButtonState(method, { statusID, message, templateID, attemptInProgress, smsBlocked, aiInProgress } = {}) {
  const label = SAVE_LABELS[method] || SAVE_LABELS.phone;
  let empty = false;
  if (method === 'phone') empty = !statusID;
  else if (method === 'sms' || method === 'payment') empty = !(typeof message === 'string' && message.trim() !== '');
  else if (method === 'email') empty = templateID === null || templateID === undefined;
  const busy = method === 'ai' ? !!aiInProgress : sendButtonDisabled(method, { attemptInProgress, smsBlocked });
  return { label, disabled: empty || busy };
}

/** The short line under a disabled method tile (the design's "why"); the full reasons stay in the panel. */
export function shortReason(method) {
  if (!method || method.enabled) return '';
  const first = (method.reasons || [])[0];
  const text = first && typeof first === 'object' ? first.text : first;
  const SHORT = {};
  SHORT[MESSAGES.smsFeatureOff] = 'Not turned on';
  SHORT[MESSAGES.emailFeatureOff] = 'Not turned on';
  SHORT[MESSAGES.smsNoMobile] = 'No mobile number';
  SHORT[MESSAGES.emailNoAddress] = 'No email on file';
  SHORT[MESSAGES.smsNotAllowed] = 'Patients only';
  SHORT[MESSAGES.emailNotAllowed] = 'Patients only';
  SHORT[MESSAGES.paymentOff] = 'Payments off';
  SHORT[MESSAGES.paymentNoTextingNumber] = 'No texting number';
  SHORT[MESSAGES.paymentNoMobile] = 'No mobile number';
  if (SHORT[text]) return SHORT[text];
  if (method.key === 'ai') return 'No mobile number';
  return text ? 'Texts not allowed' : ''; // textConsent's opt-out / Text-OK = No message
}

/**
 * The template pickers (:3024–3063). `Tags` may be a string or an array — `includes` either way, as
 * today. Email: the caller passes `emailEligiblePages` = wikiTags.withoutReviewRequestTemplates(wikiPages)
 * (review templates need the Reviews send path for {{review_link}}) AND `emailTemplateTag` — today's
 * EmailWikiPagesForFollowup tag. Both stay with the caller so template-variable-validation's PICKERS
 * guard binds to the file that really lists templates (see drawerQueues PAYMENT_TEMPLATE_TAG).
 */
export function templatesFor(method, { wikiPages, smsTemplateTag, emailEligiblePages, emailTemplateTag } = {}) {
  if (!wikiPages) return undefined;
  if (method === 'sms') return wikiPages.filter(w => w.Tags && w.Tags.includes(smsTemplateTag));
  if (method === 'email') return (emailEligiblePages || []).filter(w => w.Tags && w.Tags.includes(emailTemplateTag));
  if (method === 'payment') return wikiPages.filter(w => w.Tags && w.Tags.includes('payment'));
  return undefined;
}

/** All five, in the design's order. `textBlock` reaches Text AND Payment link (bug 4). */
export function resolveDrawerMethods(input = {}) {
  return [
    phoneMethod(input),
    smsMethod(input),
    emailMethod(input),
    paymentMethod(input),
    aiCallMethod({ isSuperAdmin: input.isSuperAdmin, followupType: input.followupType, patient: input.patient, inProgress: input.aiCallInProgress }),
  ];
}
